import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { formatDateTimeISO } from '../utils/dateUtils';
import '../styles/UserSettingsPage.css';

interface Setting {
  key: string;
  value: string | null;
  title: string;
  description: string;
  sensitive: boolean;
  updatedAt?: string;
}

interface SettingsResponse {
  settings: Setting[];
  userId: number;
}

interface SettingGroup {
  name: string;
  description: string;
  icon: string;
  settings: Setting[];
}

interface TypeaheadProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

interface SettingMetadata {
  key: string;
  title: string;
  description: string;
  sensitive: boolean;
  inputType: 'text' | 'select';
  allowedValues?: { value: string; label: string }[];
}


interface ToggleSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ checked, onChange, disabled }) => {
  return (
    <label className="toggle-switch">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="toggle-slider"></span>
    </label>
  );
};

const Typeahead: React.FC<TypeaheadProps> = ({ options, value, onChange, placeholder, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);
  const selectedLabel = selectedOption?.label || value || '';
  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(filter.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFilter('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setFilter('');
  };

  return (
    <div ref={containerRef} className={`typeahead ${className || ''}`}>
      <input
        type="text"
        value={isOpen ? filter : selectedLabel}
        onChange={(e) => setFilter(e.target.value)}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        className="setting-input"
      />
      {isOpen && (
        <div className="typeahead-dropdown">
          {filteredOptions.map((option) => (
            <div
              key={option.value}
              className={`typeahead-option ${option.value === value ? 'selected' : ''}`}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const UserSettingsPage: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<Setting[]>([]);
  const [metadata, setMetadata] = useState<SettingMetadata[]>([]);
  const [editedSettings, setEditedSettings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [settingToDelete, setSettingToDelete] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      setToast(null);

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/user-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // Capture the full response for debugging
        const responseText = await response.text();
        const errorDetails = {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          body: responseText
        };
        console.error('Settings API failed:', errorDetails);

        // Store error details in DOM for Playwright debugging
        const errorElement = document.createElement('div');
        errorElement.id = 'settings-api-error';
        errorElement.setAttribute('data-error-status', response.status.toString());
        errorElement.setAttribute('data-error-body', responseText.substring(0, 1000));
        errorElement.style.display = 'none';
        document.body.appendChild(errorElement);

        throw new Error(`Failed to fetch settings: ${response.status} - ${responseText.substring(0, 200)}...`);
      }

      const data: SettingsResponse = await response.json();
      setSettings(data.settings);

      // Initialize edited settings with current values
      const initialValues: Record<string, string> = {};
      data.settings.forEach(setting => {
        initialValues[setting.key] = setting.value || '';
      });
      setEditedSettings(initialValues);
    } catch (err) {
      console.error('Settings fetch error:', err);
      setToast({
        message: err instanceof Error ? err.message : 'Failed to load settings',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchMetadata = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/user-settings/metadata`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // Capture the full response for debugging
        const responseText = await response.text();
        const errorDetails = {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          body: responseText
        };
        console.error('Metadata API failed:', errorDetails);

        // Extra logging for CI debugging
        if (import.meta.env.VITE_CI || typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
          console.log('CRITICAL: Metadata API failed with HTML response - THIS CAUSES ONLY PREFERRED CURRENCY TO SHOW');
          console.log('Response preview:', responseText.substring(0, 500));
        }

        // Store error details in DOM for Playwright debugging
        const errorElement = document.createElement('div');
        errorElement.id = 'metadata-api-error';
        errorElement.setAttribute('data-error-status', response.status.toString());
        errorElement.setAttribute('data-error-body', responseText.substring(0, 1000));
        errorElement.style.display = 'none';
        document.body.appendChild(errorElement);

        throw new Error(`Failed to fetch metadata: ${response.status} - ${responseText.substring(0, 200)}...`);
      }

      const data: SettingMetadata[] = await response.json();
      setMetadata(data);
    } catch (err) {
      console.error('Metadata fetch error:', err);
      setToast({
        message: err instanceof Error ? err.message : 'Failed to load information about settings',
        type: 'error'
      });
    }
  }, [token]);

  const handleInputChange = (key: string, value: string) => {
    setEditedSettings(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    if (!token) return;

    try {
      setSaving(true);
      setToast(null);

      // Only send changed values
      const changedSettings: Record<string, string> = {};
      Object.entries(editedSettings).forEach(([key, value]) => {
        const original = settings.find(s => s.key === key);
        if (original && (original.value || '') !== value) {
          changedSettings[key] = value;
        }
      });

      if (Object.keys(changedSettings).length === 0) {
        setToast({
          message: 'No changes to save',
          type: 'success'
        });
        // Navigate back after showing the message
        setTimeout(() => {
          navigate(-1);
        }, 1500);
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/user-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ settings: changedSettings }),
      });

      if (!response.ok) {
        throw new Error('Failed to save settings');
      }

      // Refresh settings to get updated values first
      await fetchSettings();

      setToast({
        message: 'Settings saved successfully!',
        type: 'success'
      });

      // Navigate back on success after a short delay
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Failed to save settings',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (key: string) => {
    setSettingToDelete(key);
  };

  const confirmDelete = async () => {
    if (!token || !settingToDelete) return;

    try {
      setToast(null);

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/user-settings/${settingToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete setting');
      }

      setToast({
        message: 'Setting deleted successfully!',
        type: 'success'
      });

      // Clear the value and refresh
      setEditedSettings(prev => ({
        ...prev,
        [settingToDelete]: '',
      }));

      await fetchSettings();
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Failed to delete setting',
        type: 'error'
      });
    } finally {
      setSettingToDelete(null);
    }
  };

  const cancelDelete = () => {
    setSettingToDelete(null);
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Load settings and metadata on mount
  useEffect(() => {
    fetchSettings();
    fetchMetadata();
  }, [fetchSettings, fetchMetadata]);

  const groupSettings = (settings: Setting[]): SettingGroup[] => {
    const groups: SettingGroup[] = [
      {
        name: 'Personal Preferences',
        description: 'Your personal preferences and chat settings',
        icon: '👤',
        settings: []
      },
      {
        name: 'BrickLink',
        description: 'Connect to BrickLink marketplace for pricing and availability',
        icon: '🏪',
        settings: []
      },
      {
        name: 'Rebrickable',
        description: 'Connect to Rebrickable for enhanced LEGO data access and set list management',
        icon: '🧱',
        settings: []
      },
      {
        name: 'Brickset',
        description: 'Connect to Brickset for collection and wishlist management',
        icon: '📚',
        settings: []
      },
      {
        name: 'BrickOwl',
        description: 'Connect to BrickOwl marketplace for inventory management and catalog verification',
        icon: '🦉',
        settings: []
      },
      {
        name: 'BrickEconomy',
        description: 'Connect to BrickEconomy for market analytics and investment insights',
        icon: '📊',
        settings: []
      }
    ];

    settings.forEach(setting => {
      if (setting.key.includes('preferred_') || setting.key === 'show_detailed_tool_calls' || setting.key === 'chat_model') {
        groups[0].settings.push(setting);
      } else if (setting.key.includes('bricklink')) {
        groups[1].settings.push(setting);
      } else if (setting.key.includes('rebrickable')) {
        groups[2].settings.push(setting);
      } else if (setting.key.includes('brickset')) {
        groups[3].settings.push(setting);
      } else if (setting.key.includes('brickowl')) {
        groups[4].settings.push(setting);
      } else if (setting.key.includes('brickeconomy')) {
        groups[5].settings.push(setting);
      }
    });

    // Add currency setting at the end of personal preferences if it doesn't exist
    const currencySetting = settings.find(s => s.key === 'preferred_currency');
    if (!currencySetting) {
      groups[0].settings.push({
        key: 'preferred_currency',
        value: null,
        title: 'Preferred Currency',
        description: 'Preferred currency for pricing and cost calculations',
        sensitive: false
      });
    }

    // The API already filters admin-only settings. Show every returned integration,
    // including the user's own BrickLink and BrickOwl credentials.
    return groups.filter(group => group.settings.length > 0);
  };

  const renderSettingInput = (setting: Setting) => {
    const value = editedSettings[setting.key] || '';
    const isSensitive = setting.sensitive;
    const hasValue = setting.value && setting.value !== '';
    const isComingSoon = false; // BrickOwl integration is now available

    // Find metadata for this setting
    const settingMetadata = metadata.find(m => m.key === setting.key);

    // Check if this is a boolean setting (has exactly 2 options: true/false)
    const isBooleanSetting = settingMetadata?.allowedValues?.length === 2 &&
      settingMetadata.allowedValues.some(opt => opt.value === 'true') &&
      settingMetadata.allowedValues.some(opt => opt.value === 'false');

    // Use toggle switch for boolean settings
    if (isBooleanSetting) {
      return (
        <div className="setting-toggle-wrapper">
          <ToggleSwitch
            checked={value === 'true'}
            onChange={(checked) => handleInputChange(setting.key, checked ? 'true' : 'false')}
            disabled={isComingSoon}
          />
          <span className="setting-toggle-label">
            {value === 'true' ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      );
    }

    // If metadata indicates this is a select input and has allowed values
    if (settingMetadata?.inputType === 'select' && settingMetadata.allowedValues) {
      return (
        <Typeahead
          options={settingMetadata.allowedValues}
          value={value}
          onChange={(newValue) => handleInputChange(setting.key, newValue)}
          placeholder={`Select ${settingMetadata.title.toLowerCase()}`}
        />
      );
    }

    // Default to text input
    return (
      <input
        type={isSensitive ? 'password' : 'text'}
        value={value}
        onChange={(e) => handleInputChange(setting.key, e.target.value)}
        placeholder={isComingSoon ? 'Coming soon...' : (isSensitive && hasValue ? 'Enter new value to update' : 'Enter value')}
        className="setting-input"
        disabled={isComingSoon}
      />
    );
  };

  const settingGroups = groupSettings(settings);


  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>User Settings</h1>
        <div className="settings-header-actions">
          <button
            className="settings-save-icon"
            onClick={handleSave}
            disabled={saving}
            title={saving ? 'Saving...' : 'Save changes'}
          >
            {saving ? '⏳' : '✓'}
          </button>
          <button
            className="back-button"
            onClick={handleBack}
            title="Go back"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="settings-scroll-container">
        {loading ? (
          <div className="settings-loading">
            <div className="loading-spinner"></div>
            Loading settings...
          </div>
        ) : (
          <div className="settings-container">
            <div className="settings-content">
              <div className="settings-groups">
                {settingGroups.map((group) => (
                  <div key={group.name} className="setting-group">
                    <div className="group-header">
                      <div className="group-title">
                        <span className="group-icon">{group.icon}</span>
                        <h3>{group.name}</h3>
                      </div>
                      <p className="group-description">{group.description}</p>
                    </div>

                    <div className="group-settings">
                      {group.settings.map((setting) => (
                          <div key={setting.key} className="setting-item">
                            <div className="setting-header">
                              <label className="setting-label">
                                {setting.title || setting.key}
                                {setting.sensitive && (
                                  <span className="setting-secure" title="This value is encrypted">🔒</span>
                                )}
                              </label>
                              {setting.value && setting.value !== '' && (
                                <button
                                  className="setting-delete"
                                  onClick={() => handleDeleteClick(setting.key)}
                                  title="Delete this setting"
                                >
                                  🗑️
                                </button>
                              )}
                            </div>
                            <div className="setting-description">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {setting.description}
                              </ReactMarkdown>
                            </div>
                            <div className="setting-input-wrapper">
                              {renderSettingInput(setting)}
                            </div>
                            {setting.updatedAt && (
                              <div className="setting-updated">
                                Last updated: {formatDateTimeISO(setting.updatedAt)}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="settings-actions">
        <button
          className="settings-cancel"
          onClick={handleBack}
        >
          Cancel
        </button>
        <button
            className="settings-save"
            onClick={handleSave}
            disabled={saving}
        >
            {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <ConfirmDialog
        isOpen={settingToDelete !== null}
        title="Delete Setting"
        message="Are you sure you want to delete this setting? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default UserSettingsPage;
